/**
 * PATH: packages/ui/src/kanban/components/lane/laneVirtualizeRegistry.ts
 * FILE_ID: UI-KANBAN-LANE-VREG-001
 * PURPOSE: Module-level registry of currently-virtualized lanes so DOM-query
 *          based scroll-to-card helpers (focusCardInView, board find) can ask
 *          a lane to scroll an UNMOUNTED card into its window first.
 * WHY: Arc V7. A virtualized lane only mounts the visible window, so
 *      `document.querySelector('[data-card-id=...]')` misses off-window
 *      cards. The registry lets the (lane-agnostic) helpers bridge to the
 *      owning virtualizer's scrollToIndex, after which the normal DOM query
 *      succeeds on the next frame(s).
 * FLOW:
 * ┌──────────────┐   ┌───────────────────────┐   ┌──────────────────────┐
 * │ cardId       │ → │ find lane whose id set │ → │ scrollToIndex(index) │
 * │              │   │ contains the card      │   │ → true (bridged)     │
 * └──────────────┘   └───────────────────────┘   └──────────────────────┘
 * RELATED:
 * - UI-KANBAN-HOOK-308 (useVirtualizedLaneCards): registers/unregisters
 * - viewModeHelpers.focusCardInView: the consumer
 */

export interface VirtualizedLaneEntry {
  /** Index of the card in the lane's flattened render order, or -1. */
  indexOfCard: (cardId: string) => number
  /** Scroll the lane's window so the given index mounts. */
  scrollToIndex: (index: number) => void
}

const _registry = new Map<string, VirtualizedLaneEntry>()

export function registerVirtualizedLane(
  laneId: string,
  entry: VirtualizedLaneEntry,
): void {
  _registry.set(laneId, entry)
}

export function unregisterVirtualizedLane(laneId: string): void {
  _registry.delete(laneId)
}

/**
 * f(cardId) -> true when some virtualized lane owned the card and was told
 * to scroll it into its window (the card will mount within a frame or two);
 * false when no virtualized lane knows the card (caller falls through to
 * the plain DOM path).
 */
export function scrollVirtualizedCardIntoView(cardId: string): boolean {
  for (const entry of _registry.values()) {
    const idx = entry.indexOfCard(cardId)
    if (idx >= 0) {
      entry.scrollToIndex(idx)
      return true
    }
  }
  return false
}
